import { hashPassword } from '@/lib/passwordHash'
import { passwordProblem } from '@/lib/passwordPolicy'

/**
 * A first password for somebody else, generated here and read out once.
 *
 * The API never sees a typed password - the browser derives PBKDF2 over it
 * before anything is sent (`app/core/client_hash.py`) - so a server-generated
 * temporary password is not something it could hand back. Every admin-set
 * password in this product is therefore made in the creating browser, shown
 * on that screen, and sent only as a hash. It is a handover credential, not a
 * secret: the account is created with `mustChangePassword`, and its owner
 * replaces it at first sign-in.
 *
 * Which is also why it is generated rather than typed. A human-chosen password
 * for somebody else is reused, guessable, or both, and nobody is ever going to
 * tell the person who owns the account what rules it had to satisfy.
 */

// No 0/O, 1/l/I, or characters that a person reading one out loud has to
// explain - this value gets spoken, pasted into chat and typed back by hand.
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789'
const SYMBOLS = '!@#$%&*?+-'

function pick(from: string, count: number): string {
  const bytes = new Uint32Array(count)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => from[b % from.length]).join('')
}

/** A password that satisfies the policy, for a one-time handover. */
export function generateHandoverPassword(): string {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    // 18 characters, with upper, lower, digits and two symbols - comfortably
    // past the policy's twelve and three character classes.
    const candidate = pick(ALPHABET, 16) + pick(SYMBOLS, 2)
    const shuffled = candidate
      .split('')
      .map((c) => ({ c, k: crypto.getRandomValues(new Uint32Array(1))[0] }))
      .sort((a, b) => a.k - b.k)
      .map((x) => x.c)
      .join('')
    if (!passwordProblem(shuffled)) return shuffled
  }
  // Unreachable in practice: a random 18-character mix of four classes cannot
  // be on the common list. Returning something valid beats throwing on a form.
  return `Ix-${pick(ALPHABET, 14)}-${pick(SYMBOLS, 2)}`
}

/** The hash the API accepts, salted with the account's own email. */
export function hashFor(password: string, email: string): Promise<string> {
  return hashPassword(password, email.trim().toLowerCase())
}
